import { useHandler } from "@chatbotgang/etude/react/useHandler";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import {
  type BillingFeature,
  type BillingProduct,
  compareBillingFeature,
  FEATURE_DISPLAY_NAMES,
  PRODUCT_DISPLAY_NAMES,
} from "@polifonia/interlude/billingConstants";
import type { FeatureLimit } from "@polifonia/interlude/models";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { NumberInput } from "@/components/Input/NumberInput";
import { Switch } from "@/components/Switch";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import { useActiveBillingFeatures } from "@/features/billing/useActiveBillingFeatures";
import { useInactiveAiAgentFeatures } from "@/features/billing/useInactiveAiAgentFeatures";
import { interlude } from "@/interlude";

const DEFAULT_USAGE_LIMIT = 100;

const styles = {
  searchCard: {
    border: `1px solid ${theme.colors.neutral020}`,
    borderRadius: "12px",
  },
  searchCardContent: {
    padding: "12px 16px",
    "&:last-child": {
      paddingBottom: "12px",
    },
  },
  searchIcon: {
    color: theme.colors.neutral050,
  },
  tableContainer: {
    marginTop: "16px",
  },
  headerWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  headerCell: {
    color: theme.colors.neutral050,
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    borderBottom: `1px solid ${theme.colors.neutral020}`,
  },
  bodyRow: {
    "&:hover": {
      backgroundColor: theme.colors.neutral010,
    },
  },
  bodyCell: {
    borderBottom: `1px solid ${theme.colors.neutral010}`,
    verticalAlign: "middle",
  },
  capInput: {
    width: "120px",
    "& .MuiInputBase-input": {
      padding: "8px 12px",
    },
  },
  infoIcon: {
    color: theme.colors.neutral040,
    fontSize: "18px",
    cursor: "pointer",
  },
  savedIcon: {
    color: theme.colors.green060,
    fontSize: "16px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    py: "48px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface EditableLimit {
  /** Composite key: "product-feature" */
  key: React.Key;
  product: BillingProduct;
  feature: BillingFeature;
  isActive: boolean;
  limit: number;
  warningThresholdPercent: number;
}

const toEditableLimits = (
  featureLimits: Array<FeatureLimit>,
): Array<EditableLimit> =>
  featureLimits.map((item) => ({
    key: `${item.product}-${item.feature}`,
    product: item.product,
    feature: item.feature,
    isActive: item.isActive,
    limit: item.limit,
    warningThresholdPercent: item.warningThresholdPercent,
  }));

interface RowPayload {
  product: BillingProduct;
  feature: BillingFeature;
  limit: number;
  isActive: boolean;
}

const getSoftCap = (limit: number, percent: number): string =>
  limit > 0 ? String(Math.round((limit * percent) / 100)) : "—";

export const UsageLimitTable: React.FC = function UsageLimitTable() {
  const { t } = useTranslation();
  const activeBillingFeatures = useActiveBillingFeatures();
  const inactiveFeatures = useInactiveAiAgentFeatures();
  const { enqueueSnackbar } = useSnackbar();
  const featureLimitsQuery = interlude.billing.useGetFeatureLimits();

  const updateLimitsMutation = interlude.billing.useUpdateFeatureLimits();

  const [searchQuery, setSearchQuery] = useState("");

  const [pendingToggleKey, setPendingToggleKey] = useState<React.Key | null>(
    null,
  );
  const [savedKey, setSavedKey] = useState<React.Key | null>(null);

  const serverLimits = useMemo(
    () =>
      featureLimitsQuery.data
        ? toEditableLimits(
            featureLimitsQuery.data.featureLimits.filter(
              (limit) => !inactiveFeatures.has(limit.feature),
            ),
          )
        : [],
    [featureLimitsQuery.data, inactiveFeatures],
  );

  const [localEdits, setLocalEdits] = useState(
    () =>
      new Map<React.Key, Partial<Pick<EditableLimit, "isActive" | "limit">>>(),
  );

  const updateLocalEdit = (
    key: React.Key,
    patch: Partial<Pick<EditableLimit, "isActive" | "limit">>,
  ) => {
    setLocalEdits((prev) => {
      const next = new Map(prev);
      next.set(key, { ...prev.get(key), ...patch });
      return next;
    });
  };

  const clearLocalEdit = (key: React.Key) => {
    setLocalEdits((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const limits = useMemo(
    () =>
      serverLimits.map((item) => {
        const edit = localEdits.get(item.key);
        return edit ? { ...item, ...edit } : item;
      }),
    [serverLimits, localEdits],
  );

  const filteredLimits = useMemo(
    () =>
      limits
        .filter((limit) =>
          FEATURE_DISPLAY_NAMES[limit.feature]
            .toUpperCase()
            .includes(searchQuery.toUpperCase()),
        )
        .sort((a, b) => compareBillingFeature(a.feature, b.feature)),
    [limits, searchQuery],
  );

  const mutateRow = useHandler(
    (
      row: RowPayload,
      key: React.Key,
      extra?: { onSettled?: () => void; onSuccess?: () => void },
    ) => {
      updateLimitsMutation.mutate(
        { featureLimits: [row] },
        {
          onSuccess: () => {
            clearLocalEdit(key);
            extra?.onSuccess?.();
          },
          onError: () => {
            clearLocalEdit(key);
            enqueueSnackbar(t("common.apiError.unexpectedError"), {
              variant: "error",
            });
          },
          onSettled: () => extra?.onSettled?.(),
        },
      );
    },
  );

  const handleSearchChange = useHandler<
    React.ComponentProps<typeof TextField>["onChange"]
  >((e) => {
    setSearchQuery(e.target.value);
  });

  const handleToggleEnforce = useHandler((key: React.Key, checked: boolean) => {
    const current = limits.find((item) => item.key === key);
    if (!current) return;
    const patch: Partial<Pick<EditableLimit, "isActive" | "limit">> = {
      isActive: checked,
      ...(checked && current.limit === 0 ? { limit: DEFAULT_USAGE_LIMIT } : {}),
    };
    updateLocalEdit(key, patch);
    setPendingToggleKey(key);
    mutateRow({ ...current, ...patch }, key, {
      onSettled: () => setPendingToggleKey(null),
    });
  });

  const handleHardCapChange = useHandler((key: React.Key, value: number) => {
    updateLocalEdit(key, { limit: value });
  });

  const handleHardCapBlur = useHandler((key: React.Key) => {
    const current = limits.find((item) => item.key === key);
    const savedLimit = serverLimits.find((item) => item.key === key);
    if (!current || !savedLimit || current.limit === savedLimit.limit) return;
    mutateRow(current, key, {
      onSuccess: () => {
        setSavedKey(key);
        setTimeout(() => setSavedKey(null), 1500);
      },
    });
  });

  if (featureLimitsQuery.isPending) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (featureLimitsQuery.isError) {
    return (
      <Box sx={styles.loadingContainer}>
        <Typography variant="body">{t("billing.error.generic")}</Typography>
      </Box>
    );
  }

  return (
    <Stack>
      <Card variant="outlined" sx={styles.searchCard}>
        <CardContent sx={styles.searchCardContent}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("billing.alertSetting.usageLimit.searchPlaceholder")}
            value={searchQuery}
            onChange={handleSearchChange}
            variant="standard"
            slotProps={{
              input: {
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={styles.searchIcon} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </CardContent>
      </Card>
      <TableContainer sx={styles.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.headerCell}>
                {t("billing.alertSetting.usageLimit.featureName")}
              </TableCell>
              <TableCell sx={styles.headerCell}>
                {t("billing.alertSetting.usageLimit.product")}
              </TableCell>
              <TableCell sx={styles.headerCell}>
                {t("billing.alertSetting.usageLimit.enforceLimit")}
              </TableCell>
              <TableCell sx={styles.headerCell}>
                {t("billing.alertSetting.usageLimit.hardCap")}
              </TableCell>
              <TableCell sx={styles.headerCell}>
                <Box sx={styles.headerWithIcon}>
                  {t("billing.alertSetting.usageLimit.softCap")}
                  <Tooltip
                    title={t("billing.alertSetting.usageLimit.softCapTooltip")}
                    arrow
                  >
                    <InfoOutlinedIcon sx={styles.infoIcon} />
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLimits.map((limit) => {
              const isFeatureSupported = activeBillingFeatures.has(
                limit.feature,
              );
              return (
                <TableRow key={limit.key} sx={styles.bodyRow}>
                  <TableCell sx={styles.bodyCell}>
                    <Typography variant="body">
                      {FEATURE_DISPLAY_NAMES[limit.feature]}
                    </Typography>
                    {!isFeatureSupported && (
                      <Typography
                        variant="note"
                        color={theme.colors.neutral050}
                      >
                        {t("billing.usageLimit.featureNotAvailable")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={styles.bodyCell}>
                    <Typography variant="body">
                      {PRODUCT_DISPLAY_NAMES[limit.product]}
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.bodyCell}>
                    <Switch
                      checked={limit.isActive}
                      onChange={(e) =>
                        handleToggleEnforce(limit.key, e.target.checked)
                      }
                      loading={pendingToggleKey === limit.key}
                      disabled={
                        !isFeatureSupported || updateLimitsMutation.isPending
                      }
                    />
                  </TableCell>
                  <TableCell sx={styles.bodyCell}>
                    <NumberInput
                      key={limit.key}
                      value={limit.limit}
                      min={1}
                      onChange={(value) =>
                        handleHardCapChange(limit.key, value)
                      }
                      onBlur={() => handleHardCapBlur(limit.key)}
                      disabled={
                        !isFeatureSupported ||
                        !limit.isActive ||
                        updateLimitsMutation.isPending
                      }
                      size="small"
                      sx={styles.capInput}
                      slotProps={{
                        input: {
                          endAdornment:
                            savedKey === limit.key ? (
                              <InputAdornment position="end">
                                <CheckCircleRoundedIcon sx={styles.savedIcon} />
                              </InputAdornment>
                            ) : undefined,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={styles.bodyCell}>
                    <Typography
                      variant="body"
                      color={
                        !isFeatureSupported || !limit.isActive
                          ? theme.colors.neutral040
                          : undefined
                      }
                    >
                      {isFeatureSupported
                        ? getSoftCap(limit.limit, limit.warningThresholdPercent)
                        : "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
